"use client";

import { useState, type ReactNode } from "react";
import dynamic from "next/dynamic";
import type { TerminalChartData } from "./types";

/**
 * Lazy boundary for the terminal chart with a real SSR/no-JS fallback.
 *
 * `lightweight-charts` (~45KB gz) + the chart component are code-split into
 * their own async chunk loaded only when an order-flow lesson renders a chart,
 * so risk-only lesson pages never ship the charting runtime.
 *
 * The `fallback` (a server-rendered SVG figure for the same concept) is what
 * the server emits and what no-JS / pre-hydration / crawler users see. It is
 * overlaid on top of the (full-width, correctly sized) chart container and
 * removed once the canvas chart reports ready, so there is no blank flash and
 * the chart mounts at the right width. With JS disabled the SVG figure stays.
 */
const Inner = dynamic(
  () => import("./TerminalChart").then((m) => m.TerminalChart),
  { ssr: false },
);

export function LazyTerminalChart({
  data,
  caption,
  height,
  className,
  terminalLabel,
  fallback,
}: {
  data: TerminalChartData;
  caption?: string;
  height?: number;
  className?: string;
  /** Watermark passthrough (e.g. "price structure terminal"). */
  terminalLabel?: string;
  /** Server-rendered SVG figure shown until the canvas chart is ready. */
  fallback: ReactNode;
}) {
  const [ready, setReady] = useState(false);
  const minHeight = data.cvd && data.cvd.length > 0 ? 520 : height ?? 380;

  return (
    <div className={className} style={{ position: "relative", minHeight }}>
      <div>
        <Inner
          data={data}
          caption={caption}
          height={height}
          terminalLabel={terminalLabel}
          onReady={() => setReady(true)}
        />
      </div>
      {!ready ? (
        <div style={{ position: "absolute", inset: 0 }}>{fallback}</div>
      ) : null}
    </div>
  );
}
